import {
  ActionIcon,
  Anchor,
  AppShell,
  Burger,
  Flex,
  Group,
  Image,
  Menu,
  rem,
  Stack,
  Text,
  useComputedColorScheme,
  useMantineColorScheme
} from "@mantine/core"
import { useDisclosure, useHotkeys, useLocalStorage } from "@mantine/hooks"
import { showNotification } from "@mantine/notifications"
import { IconAd2, IconMoon, IconSun } from "@tabler/icons-react"
import { useEffect, useState } from "react"
import { useTranslation } from "react-i18next"
import { Link, Outlet, ScrollRestoration, useNavigate } from "react-router"
import { renderFooter } from "./Commons/Renders"
import { LanguagePicker } from "./Components/LanguagePicker/LanguagePicker"

import classes from "./App.module.css"

function App() {
  const navigate = useNavigate()
  const { i18n, t } = useTranslation()

  const [opened, { toggle }] = useDisclosure()
  const [active, setActive] = useState(-99)
  const [language, setLanguage] = useLocalStorage<string>({
    key: "language",
    defaultValue: "en"
  })

  const [adsNoticeDismissed, setAdsNoticeDismissed] = useLocalStorage<boolean>({
    key: "ads-notice-dismissed",
    defaultValue: false
  })

  useEffect(() => {
    i18n.changeLanguage(language)
  }, [language])

  const { toggleColorScheme } = useMantineColorScheme()
  const computedColorScheme = useComputedColorScheme("light", {
    getInitialValueInEffect: true
  })

  interface INavLink {
    index: number
    label: string
    path: string
    disabled?: boolean
  }

  const mainLinks: INavLink[] = [
    { index: 1, label: t("nav.downloads"), path: "/files" },
    { index: 2, label: t("nav.team"), path: "/team" },
    { index: 3, label: t("nav.community"), path: "/community" },
    { index: 4, label: t("nav.wiki"), path: "/wiki" }
  ]

  const navLinks: INavLink[] = [
    { index: 5, label: "Aurora Store", path: "/aurora-store" },
    { index: 6, label: "App Warden", path: "/app-warden", disabled: true },
    { index: 7, label: "Config Generator", path: "/config-generator" },
    ...mainLinks
  ]

  useHotkeys([["mod + J", () => toggleColorScheme()]])

  useEffect(() => {
    const timer = setTimeout(() => {
      if (!adsNoticeDismissed) {
        showNotification({
          id: "ads-notice",
          title: "Just a heads-up!",
          message: `We've added ads to the website to help keep the project running.\n\nWe're forever committed to keeping the Aurora Store app ad-free.\n\nThanks for your support! ♥️`,
          icon: <IconAd2 />,
          color: "primary",
          autoClose: false,
          withCloseButton: true,
          closeButtonProps: { "aria-label": "close-ads-notice" },
          onClose: () => setAdsNoticeDismissed(true),
          style: { whiteSpace: "pre-line" }
        })
      }
    }, 500)

    return () => clearTimeout(timer)
  }, [adsNoticeDismissed, setAdsNoticeDismissed])

  return (
    <AppShell
      header={{ height: rem(60), offset: true }}
      navbar={{ width: 300, breakpoint: "sm", collapsed: { desktop: true, mobile: !opened } }}
      transitionDuration={200}
      transitionTimingFunction="ease"
      withBorder={false}
      padding={{ base: "sm", sm: "md", lg: "md" }}
    >
      <AppShell.Header className={classes.header}>
        <Group p={"xs"} align={"center"} justify="space-between" maw={rem(1440)} mx="auto">
          <Group>
            <Burger opened={opened} onClick={toggle} hiddenFrom="sm" size="sm" aria-label="nav-toggle" />
            <ActionIcon
              variant={"transparent"}
              color={"dimmed"}
              radius={0}
              onClick={() => {
                navigate("/")
                setActive(-99)
              }}
            >
              <Image h={24} w={"auto"} alt="logo" src={"/favicon.svg"} />
            </ActionIcon>
          </Group>

          <Flex gap={0} align={"center"} justify={"center"} visibleFrom="sm">
            <Menu shadow="md" width={200} offset={2} withArrow>
              <Menu.Target>
                <Text className={classes.mainLink} data-active={active >= 5 || undefined} aria-controls="nav-link-apps">
                  {t("nav.apps")}
                </Text>
              </Menu.Target>
              <Menu.Dropdown>
                <Menu.Label fz={rem(10)} c={"primary.5"}>
                  Apps
                </Menu.Label>
                <Menu.Item
                  onClick={() => {
                    navigate("/aurora-store")
                    setActive(5)
                  }}
                >
                  Aurora Store
                </Menu.Item>
                <Menu.Item disabled>App Warden</Menu.Item>
                <Menu.Divider />
                <Menu.Label fz={rem(10)} c={"primary.5"}>
                  Tools
                </Menu.Label>
                <Menu.Item
                  onClick={() => {
                    navigate("/config-generator")
                    setActive(7)
                  }}
                >
                  Config Generator
                </Menu.Item>
              </Menu.Dropdown>
            </Menu>

            {mainLinks.map((link) => (
              <Anchor
                component={Link}
                to={link.path}
                key={link.index}
                className={classes.mainLink}
                data-active={link.index === active || undefined}
                aria-label={`header-link-${link.label.toLowerCase()}`}
                onClick={() => {
                  setActive(link.index)
                }}
              >
                {link.label}
              </Anchor>
            ))}
          </Flex>

          <Group justify="flex-end" align="center" gap={"sm"}>
            <LanguagePicker currentLanguage={language} onChange={(language: any) => setLanguage(language.code)} />

            <ActionIcon
              size={rem(36)}
              variant="default"
              onClick={() => toggleColorScheme()}
              aria-label="toggle-color-scheme"
            >
              {computedColorScheme === "dark" ? (
                <IconMoon size={20} stroke={1.5} />
              ) : (
                <IconSun size={20} stroke={1.5} />
              )}
            </ActionIcon>
          </Group>
        </Group>
      </AppShell.Header>

      <AppShell.Navbar>
        <Stack gap={rem(6)}>
          {navLinks
            .filter((link) => !link.disabled)
            .map((link) => (
              <Anchor
                component={Link}
                to={link.path}
                key={link.index}
                className={classes.navLink}
                data-active={link.index === active || undefined}
                aria-label={`nav-link-${link.label.toLowerCase()}`}
                onClick={() => {
                  setActive(link.index)
                  toggle()
                }}
              >
                {link.label}
              </Anchor>
            ))}
        </Stack>
      </AppShell.Navbar>

      <AppShell.Main maw={rem(1440)} mx="auto" className={classes.content}>
        <ScrollRestoration />
        <Outlet />
      </AppShell.Main>
      {renderFooter()}
    </AppShell>
  )
}

export default App
